import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Activity,
  ArrowDown,
  ArrowUp,
  BarChart3,
  CalendarClock,
  Clock3,
  ExternalLink,
  Filter,
  Gauge,
  Globe2,
  Heart,
  LineChart,
  Music2,
  Newspaper,
  PieChart,
  Play,
  Radio,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Sparkles,
  Trophy,
  Users,
  X,
} from "lucide-react";
import type { DashboardData, Entry } from "./types";
import { loadDashboardData } from "./lib/data";
import { compact, dateTime, number, pct, pctWhole, rankLabel, signed } from "./lib/format";
import { ProbabilityBars, SignalRadar, Sparkline, SynergyBars } from "./components/Charts";
import DotField from "./components/DotField";

type SortKey = "rank" | "country" | "win" | "top3" | "jury" | "audience" | "market" | "odds" | "youtube" | "spotify" | "poll";
type SortDir = "asc" | "desc";
type AppRoute = "dashboard" | "betting" | "news";
type FilterKey = "all" | "winner" | "top5" | "jury" | "audience" | "risk";
type NumericFilterKey =
  | "winMin"
  | "winMax"
  | "kalshiMin"
  | "kalshiMax"
  | "spotifyMin"
  | "spotifyMax"
  | "youtubeMin"
  | "youtubeMax"
  | "pollMin"
  | "pollMax"
  | "juryMin"
  | "juryMax"
  | "audienceMin"
  | "audienceMax";

type NumericFilters = Record<NumericFilterKey, string>;

const filterLabels: Record<FilterKey, string> = {
  all: "All",
  winner: "Winner path",
  top5: "Top 5",
  jury: "Jury race",
  audience: "Audience race",
  risk: "Disagreement",
};

const emptyNumericFilters: NumericFilters = {
  winMin: "",
  winMax: "",
  kalshiMin: "",
  kalshiMax: "",
  spotifyMin: "",
  spotifyMax: "",
  youtubeMin: "",
  youtubeMax: "",
  pollMin: "",
  pollMax: "",
  juryMin: "",
  juryMax: "",
  audienceMin: "",
  audienceMax: "",
};

const defaultSortDir: Record<SortKey, SortDir> = {
  rank: "asc",
  country: "asc",
  win: "desc",
  top3: "desc",
  jury: "asc",
  audience: "asc",
  market: "desc",
  odds: "asc",
  youtube: "desc",
  spotify: "desc",
  poll: "desc",
};

type ParsedNumericFilters = {
  winMin: number | null;
  winMax: number | null;
  kalshiMin: number | null;
  kalshiMax: number | null;
  spotifyMin: number | null;
  spotifyMax: number | null;
  youtubeMin: number | null;
  youtubeMax: number | null;
  pollMin: number | null;
  pollMax: number | null;
  juryMin: number | null;
  juryMax: number | null;
  audienceMin: number | null;
  audienceMax: number | null;
};

function parseNumericFilters(filters: NumericFilters): ParsedNumericFilters {
  return {
    winMin: parseProbabilityInput(filters.winMin),
    winMax: parseProbabilityInput(filters.winMax),
    kalshiMin: parseProbabilityInput(filters.kalshiMin),
    kalshiMax: parseProbabilityInput(filters.kalshiMax),
    spotifyMin: parseCountInput(filters.spotifyMin),
    spotifyMax: parseCountInput(filters.spotifyMax),
    youtubeMin: parseCountInput(filters.youtubeMin),
    youtubeMax: parseCountInput(filters.youtubeMax),
    pollMin: parseCountInput(filters.pollMin),
    pollMax: parseCountInput(filters.pollMax),
    juryMin: parseCountInput(filters.juryMin),
    juryMax: parseCountInput(filters.juryMax),
    audienceMin: parseCountInput(filters.audienceMin),
    audienceMax: parseCountInput(filters.audienceMax),
  };
}

function parseCountInput(value: string): number | null {
  const normalized = value.trim().toLowerCase().replaceAll(",", "");
  if (!normalized) return null;

  const match = normalized.match(/^(\d+(?:\.\d+)?)([kmb])?$/);
  if (!match) return null;

  const base = Number(match[1]);
  const suffix = match[2];
  if (suffix === "k") return base * 1_000;
  if (suffix === "m") return base * 1_000_000;
  if (suffix === "b") return base * 1_000_000_000;
  return base;
}

function parseProbabilityInput(value: string): number | null {
  const parsed = parseCountInput(value.replace("%", ""));
  if (parsed === null) return null;
  return parsed > 1 ? parsed / 100 : parsed;
}

function inRange(value: number, min: number | null, max: number | null) {
  if (min !== null && value < min) return false;
  if (max !== null && value > max) return false;
  return true;
}

function passesNumericFilters(entry: Entry, filters: ParsedNumericFilters) {
  return (
    inRange(entry.winner_probability, filters.winMin, filters.winMax) &&
    inRange(entry.kalshi_last_price || 0, filters.kalshiMin, filters.kalshiMax) &&
    inRange(entry.spotify_stream_count, filters.spotifyMin, filters.spotifyMax) &&
    inRange(entry.youtube_total_views, filters.youtubeMin, filters.youtubeMax) &&
    inRange(entry.poll_votes, filters.pollMin, filters.pollMax) &&
    inRange(entry.estimated_jury_rank, filters.juryMin, filters.juryMax) &&
    inRange(entry.estimated_audience_rank, filters.audienceMin, filters.audienceMax)
  );
}

function sortValue(entry: Entry, sort: SortKey): string | number {
  if (sort === "country") return entry.country;
  if (sort === "win") return entry.winner_probability;
  if (sort === "top3") return entry.top3_probability;
  if (sort === "jury") return entry.estimated_jury_rank;
  if (sort === "audience") return entry.estimated_audience_rank;
  if (sort === "market") return entry.kalshi_last_price || 0;
  if (sort === "odds") return entry.ew_odds_rank || 999;
  if (sort === "youtube") return entry.youtube_total_views;
  if (sort === "spotify") return entry.spotify_stream_count;
  if (sort === "poll") return entry.poll_votes;
  return entry.predicted_rank;
}

function App() {
  const [data, setData] = useState<DashboardData | null>(null);
  const route = currentRoute();
  const [selectedId, setSelectedId] = useState<string>("finland");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("rank");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [numericFilters, setNumericFilters] = useState<NumericFilters>(emptyNumericFilters);
  const [inspectorOpen, setInspectorOpen] = useState(false);

  useEffect(() => {
    loadDashboardData().then((bundle) => {
      setData(bundle);
      setSelectedId(bundle.entries[0]?.country_id || "finland");
    });
  }, []);

  useEffect(() => {
    if (!inspectorOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setInspectorOpen(false);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [inspectorOpen]);

  const entries = data?.entries || [];
  const selected = entries.find((entry) => entry.country_id === selectedId) || entries[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const parsedFilters = parseNumericFilters(numericFilters);
    const rows = entries.filter((entry) => {
      const matches =
        !q ||
        entry.country.toLowerCase().includes(q) ||
        entry.artist.toLowerCase().includes(q) ||
        entry.song.toLowerCase().includes(q) ||
        entry.manual_win_read.toLowerCase().includes(q);
      if (!matches) return false;
      const matchesPreset =
        filter === "all" ||
        (filter === "winner" && (entry.winner_probability >= 0.02 || entry.top3_probability >= 0.25)) ||
        (filter === "top5" && entry.top5_probability >= 0.25) ||
        (filter === "jury" && (entry.jury_winner_probability >= 0.01 || entry.estimated_jury_rank <= 8)) ||
        (filter === "audience" && (entry.audience_winner_probability >= 0.01 || entry.estimated_audience_rank <= 8)) ||
        (filter === "risk" && Math.abs((entry.ew_winning_chance_percent || 0) / 100 - entry.winner_probability) >= 0.035);

      return matchesPreset && passesNumericFilters(entry, parsedFilters);
    });

    return [...rows].sort((a, b) => {
      const aValue = sortValue(a, sort);
      const bValue = sortValue(b, sort);
      const direction = sortDir === "asc" ? 1 : -1;

      if (typeof aValue === "string" || typeof bValue === "string") {
        return String(aValue).localeCompare(String(bValue)) * direction;
      }

      return ((aValue as number) - (bValue as number)) * direction;
    });
  }, [entries, filter, numericFilters, query, sort, sortDir]);

  function updateSort(nextSort: SortKey) {
    if (nextSort === sort) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSort(nextSort);
    setSortDir(defaultSortDir[nextSort]);
  }

  if (!data || !selected) {
    return (
      <main className="app-shell">
        <AmbientField />
        <div className="page" style={{ padding: 32 }}>
          <div className="surface">Loading Eurovision signal room...</div>
        </div>
      </main>
    );
  }

  const top = entries[0];
  const topFive = entries.slice(0, 5);
  const modelEdge = (top.winner_probability - (entries[1]?.winner_probability || 0)) * 100;

  return (
    <main className="app-shell">
      <AmbientField />
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <div className="brand-mark">
              <Trophy size={19} />
            </div>
            <div>
              <h1>ESC26 Signal Room</h1>
              <p>Grand Final prediction intelligence</p>
            </div>
          </div>
          <nav className="topnav" aria-label="Primary">
            <a className={route === "dashboard" ? "active" : ""} href="/">
              <BarChart3 size={15} />
              Dashboard
            </a>
            <a className={route === "betting" ? "active" : ""} href="/betting">
              <PieChart size={15} />
              Bet plan
            </a>
            <a className={route === "news" ? "active" : ""} href="/news">
              <Newspaper size={15} />
              News flow
            </a>
          </nav>
          {route === "dashboard" ? (
            <div className="toolbar">
            <label className="searchbox">
              <Search size={16} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Country, artist, signal..." />
            </label>
            <label className="control">
              <Filter size={16} />
              <select value={filter} onChange={(event) => setFilter(event.target.value as FilterKey)}>
                {Object.entries(filterLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="control">
              <BarChart3 size={16} />
              <select
                value={sort}
                onChange={(event) => {
                  const nextSort = event.target.value as SortKey;
                  setSort(nextSort);
                  setSortDir(defaultSortDir[nextSort]);
                }}
              >
                <option value="rank">Model rank</option>
                <option value="country">Country</option>
                <option value="win">Win probability</option>
                <option value="top3">Top 3 probability</option>
                <option value="jury">Jury rank</option>
                <option value="audience">Audience rank</option>
                <option value="market">Kalshi</option>
                <option value="odds">Odds rank</option>
                <option value="youtube">YouTube</option>
                <option value="spotify">Spotify</option>
                <option value="poll">Poll</option>
              </select>
            </label>
            </div>
          ) : (
            <div className="toolbar">
              <a className="control nav-control" href="/">
                <BarChart3 size={16} />
                Back to dashboard
              </a>
            </div>
          )}
        </div>
      </header>

      {route === "betting" ? (
        <BettingPage entries={entries} generatedAt={data.generatedAt} />
      ) : route === "news" ? (
        <NewsFlowPage entries={entries} />
      ) : (
      <div className="page">
        <section className="hero">
          <motion.div className="hero-main" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <div className="hero-copy">
              <div>
                <div className="eyebrow">
                  <Sparkles size={15} />
                  Baseline v0 · {dateTime(data.generatedAt)}
                </div>
                <h2 className="hero-title">
                  {top.country} <span>controls</span> the board
                </h2>
                <p className="hero-subtitle">
                  A model stitched from your manual read, odds, Kalshi trades, poll heat, Spotify, YouTube, running order, and historical voting affinity.
                  This is the working command surface for deciding where the forecast is right, fragile, or lying.
                </p>
              </div>
              <div className="hero-metrics">
                <Metric label="Win probability" value={pct(top.winner_probability)} />
                <Metric label="Top 3" value={pct(top.top3_probability)} />
                <Metric label="Lead over #2" value={`${modelEdge.toFixed(1)} pts`} />
              </div>
            </div>
            <div className="leader-panel">
              <div className="leader-image">
                <img src={top.heroImage} alt={`${top.country} performance thumbnail`} />
                <div className="leader-caption">
                  <span>{top.flagEmoji} {top.artist}</span>
                  <strong>{top.song}</strong>
                </div>
              </div>
              <ProbabilityBars entry={top} />
            </div>
          </motion.div>

          <aside className="rail">
            <section className="surface">
              <div className="section-title">
                <h3>Top Five</h3>
                <span>model order</span>
              </div>
              <div className="top-five">
                {topFive.map((entry) => (
                  <button
                    key={entry.country_id}
                    className="top-five-row"
                    onClick={() => {
                      setSelectedId(entry.country_id);
                      setInspectorOpen(true);
                    }}
                  >
                    <span className="rank-token">{entry.predicted_rank}</span>
                    <span className="country-line">
                      <strong>{entry.flagEmoji} {entry.country}</strong>
                      <span>{pct(entry.winner_probability)} win · {pct(entry.top5_probability)} top 5</span>
                    </span>
                    <span className="bar-track">
                      <span className="bar-fill" style={{ width: `${entry.winner_probability * 100}%` }} />
                    </span>
                  </button>
                ))}
              </div>
            </section>
            <section className="surface">
              <div className="section-title">
                <h3>Market Tension</h3>
                <span>Kalshi vs model</span>
              </div>
              <TensionList entries={entries} />
            </section>
          </aside>
        </section>

        <section className="dashboard-grid">
          <section className="surface table-shell">
            <div className="section-title">
              <h2>Final Board</h2>
              <span>{filtered.length} of {entries.length} countries</span>
            </div>
            <FilterBench
              filters={numericFilters}
              onChange={(key, value) => setNumericFilters((current) => ({ ...current, [key]: value }))}
              onReset={() => setNumericFilters(emptyNumericFilters)}
            />
            <ScoreTable
              entries={filtered}
              selectedId={selected.country_id}
              sort={sort}
              sortDir={sortDir}
              onSort={updateSort}
              onSelect={(countryId) => {
                setSelectedId(countryId);
                setInspectorOpen(true);
              }}
            />
          </section>
        </section>
      </div>
      )}

      <AnimatePresence>
        {inspectorOpen ? (
          <InspectorDrawer entry={selected} onClose={() => setInspectorOpen(false)} />
        ) : null}
      </AnimatePresence>
    </main>
  );
}

function currentRoute(): AppRoute {
  const pathname = window.location.pathname;
  if (pathname.startsWith("/betting")) return "betting";
  if (pathname.startsWith("/news")) return "news";
  return "dashboard";
}

type LiveMarket = {
  ticker: string;
  event_ticker?: string | null;
  title?: string | null;
  subtitle?: string | null;
  yes_sub_title?: string | null;
  no_sub_title?: string | null;
  yes_bid?: number | string | null;
  yes_ask?: number | string | null;
  no_bid?: number | string | null;
  no_ask?: number | string | null;
  last_price?: number | string | null;
  yes_bid_dollars?: number | string | null;
  yes_ask_dollars?: number | string | null;
  no_bid_dollars?: number | string | null;
  no_ask_dollars?: number | string | null;
  last_price_dollars?: number | string | null;
  volume_24h?: number | string | null;
  volume_24h_fp?: number | string | null;
  open_interest?: number | string | null;
  open_interest_fp?: number | string | null;
  updated_time?: string | null;
};

type MarketFamily = "winner" | "jury" | "televote" | "top3" | "top5" | "top10";
type ContractSide = "YES" | "NO";

type BetContract = {
  id: string;
  ticker: string;
  entry: Entry;
  family: MarketFamily;
  label: string;
  side: ContractSide;
  modelProbability: number;
  marketPrice: number;
  edge: number;
  edgeRatio: number;
  source: "live ask" | "synthetic snapshot";
};

type Scenario = {
  id: "low" | "medium" | "high";
  name: string;
  tagline: string;
  maxPositions: number;
  maxWeight: number;
  allowNo: boolean;
  minModelProbability: number;
  minEdge: number;
  score: (contract: BetContract) => number;
};

const scenarios: Scenario[] = [
  {
    id: "low",
    name: "Low Risk",
    tagline: "Mostly Finland, with only the clearest positive-edge hedges.",
    maxPositions: 7,
    maxWeight: 0.64,
    allowNo: false,
    minModelProbability: 0.18,
    minEdge: 0.035,
    score: (contract) => Math.max(contract.edge, 0) ** 1.06 * contract.modelProbability ** 0.9 * Math.min(contract.edgeRatio, 3),
  },
  {
    id: "medium",
    name: "Medium Risk",
    tagline: "Winner/top-rank upside, plus selective NO contracts where the market is too generous.",
    maxPositions: 11,
    maxWeight: 0.46,
    allowNo: true,
    minModelProbability: 0.08,
    minEdge: 0.025,
    score: (contract) =>
      Math.max(contract.edge, 0) ** 1.12 * Math.sqrt(contract.modelProbability) * Math.pow(Math.min(contract.edgeRatio, 5), 0.72),
  },
  {
    id: "high",
    name: "High Risk",
    tagline: "Aggressive mispricing hunt across winner, jury, televote, top 3, top 5, top 10, YES and NO.",
    maxPositions: 16,
    maxWeight: 0.34,
    allowNo: true,
    minModelProbability: 0.025,
    minEdge: 0.012,
    score: (contract) =>
      Math.max(contract.edge, 0) ** 1.16 * Math.pow(Math.min(contract.edgeRatio, 8), 1.05) * Math.pow(1 / contract.marketPrice, 0.28),
  },
];

type FirehoseRule = {
  id: string;
  value?: string;
  tag?: string;
};

type NewsFlowItem = {
  id: string;
  queryId: string;
  ruleTag: string;
  matchedAt: string;
  publishTime: string;
  title: string;
  url: string;
  domain: string;
  language: string;
  categories: string[];
  types: string[];
  snippet: string;
};

type StreamStatus = "connecting" | "live" | "reconnecting" | "ended" | "error";

function NewsFlowPage({ entries }: { entries: Entry[] }) {
  const [items, setItems] = useState<NewsFlowItem[]>([]);
  const [rules, setRules] = useState<FirehoseRule[]>([]);
  const [status, setStatus] = useState<StreamStatus>("connecting");
  const [query, setQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [streamEpoch, setStreamEpoch] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadRules() {
      try {
        const response = await fetch("/api/firehose-rules", { cache: "no-store" });
        const payload = await response.json();
        if (!cancelled && Array.isArray(payload?.data)) setRules(payload.data);
      } catch {
        if (!cancelled) setRules([]);
      }
    }

    loadRules();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setStatus("connecting");
    const source = new EventSource("/api/firehose?since=24h&timeout=300&limit=500");
    let reconnectTimer = 0;

    source.addEventListener("connected", () => setStatus("live"));
    source.addEventListener("update", (event) => {
      const item = normalizeFirehoseItem(event as MessageEvent, rules);
      if (!item) return;
      setStatus("live");
      setLastUpdate(item.matchedAt);
      setItems((current) => {
        const next = [item, ...current.filter((existing) => existing.id !== item.id)];
        return next.slice(0, 180);
      });
    });
    source.addEventListener("end", () => {
      setStatus("ended");
      source.close();
      reconnectTimer = window.setTimeout(() => setStreamEpoch((current) => current + 1), 1200);
    });
    source.addEventListener("error", () => setStatus("reconnecting"));
    source.onerror = () => setStatus("reconnecting");

    return () => {
      window.clearTimeout(reconnectTimer);
      source.close();
    };
  }, [rules, streamEpoch]);

  const countryPulse = useMemo(() => buildCountryPulse(items, entries), [entries, items]);
  const filteredItems = useMemo(() => {
    const normalizedQuery = normalizeText(query);
    return items.filter((item) => {
      const haystack = normalizeText(`${item.title} ${item.snippet} ${item.domain} ${item.ruleTag}`);
      const matchesQuery = !normalizedQuery || haystack.includes(normalizedQuery);
      const matchesCountry =
        countryFilter === "all" || countryHitText(item, entries.find((entry) => entry.country_id === countryFilter) || null);
      return matchesQuery && matchesCountry;
    });
  }, [countryFilter, entries, items, query]);

  const selectedCountry = countryFilter === "all" ? null : entries.find((entry) => entry.country_id === countryFilter) || null;

  return (
    <div className="page news-page">
      <section className="news-hero surface">
        <div>
          <div className="eyebrow">
            <Radio size={15} />
            Firehose live stream
          </div>
          <h2 className="hero-title">
            Incoming <span>news</span> wire
          </h2>
          <p className="hero-subtitle">
            Real-time web matches from the active Firehose tap, replaying the last 24 hours on load and then tailing live updates. Use it to spot late narratives,
            press cycles, and sudden country-specific attention before folding them into the prediction read.
          </p>
        </div>
        <div className="stream-panel">
          <div className={`stream-status ${status}`}>
            <span />
            {statusLabel(status)}
          </div>
          <Metric label="Items captured" value={number(items.length)} />
          <Metric label="Active rules" value={number(rules.length)} />
          <Metric label="Last update" value={lastUpdate ? shortTime(lastUpdate) : "waiting"} />
        </div>
      </section>

      <section className="news-controls surface">
        <label className="searchbox">
          <Search size={16} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search title, source, snippet..." />
        </label>
        <label className="control">
          <Globe2 size={16} />
          <select value={countryFilter} onChange={(event) => setCountryFilter(event.target.value)}>
            <option value="all">All country signals</option>
            {entries.map((entry) => (
              <option key={entry.country_id} value={entry.country_id}>
                {entry.flagEmoji} {entry.country}
              </option>
            ))}
          </select>
        </label>
        <div className="news-scope">
          <span>Replay: 24h</span>
          <span>Timeout: 300s</span>
          <span>Limit: 500</span>
        </div>
      </section>

      <section className="news-layout">
        <aside className="surface pulse-panel">
          <div className="section-title">
            <h3>Country Pulse</h3>
            <span>{selectedCountry ? selectedCountry.country : "feed mentions"}</span>
          </div>
          <div className="pulse-list">
            {countryPulse.length ? (
              countryPulse.map((row) => (
                <button key={row.entry.country_id} type="button" className="pulse-row" onClick={() => setCountryFilter(row.entry.country_id)}>
                  <span>{row.entry.flagEmoji} {row.entry.country}</span>
                  <div className="bar-track">
                    <span className="bar-fill" style={{ width: `${row.share * 100}%` }} />
                  </div>
                  <strong>{row.count}</strong>
                </button>
              ))
            ) : (
              <div className="empty">No country-level mentions in the current stream buffer yet.</div>
            )}
          </div>
        </aside>

        <section className="surface feed-panel">
          <div className="section-title">
            <h2>Live Items</h2>
            <span>{filteredItems.length} visible</span>
          </div>
          <div className="feed-list">
            {filteredItems.length ? (
              filteredItems.map((item) => <FeedItem key={item.id} item={item} />)
            ) : (
              <div className="empty">Waiting for Firehose matches. If this stays empty, check that the tap has rules configured.</div>
            )}
          </div>
        </section>
      </section>
    </div>
  );
}

function FeedItem({ item }: { item: NewsFlowItem }) {
  return (
    <article className="feed-item">
      <div className="feed-item-top">
        <div>
          <a href={item.url} target="_blank" rel="noreferrer">
            {item.title}
            <ExternalLink size={14} />
          </a>
          <div className="feed-meta">
            <span>
              <Globe2 size={13} />
              {item.domain || "unknown source"}
            </span>
            <span>
              <Clock3 size={13} />
              {dateTime(item.matchedAt)}
            </span>
            {item.ruleTag ? <span>{item.ruleTag}</span> : null}
            {item.language ? <span>{item.language}</span> : null}
          </div>
        </div>
        <span className="pill gold">{item.queryId || "rule"}</span>
      </div>
      <p>{item.snippet || "No excerpt was included in the Firehose update payload."}</p>
      <div className="feed-tags">
        {[...item.categories, ...item.types].slice(0, 4).map((tag) => (
          <span key={tag}>{tag}</span>
        ))}
      </div>
    </article>
  );
}

function BettingPage({ entries, generatedAt }: { entries: Entry[]; generatedAt: string }) {
  const [budget, setBudget] = useState(1000);
  const [liveMarkets, setLiveMarkets] = useState<Record<string, LiveMarket>>({});
  const [marketCount, setMarketCount] = useState(0);
  const [liveStatus, setLiveStatus] = useState<"loading" | "live" | "snapshot">("loading");

  useEffect(() => {
    let cancelled = false;

    async function loadLiveMarkets() {
      try {
        const response = await fetch("/api/kalshi", { cache: "no-store" });
        if (!response.ok) throw new Error(`Kalshi proxy ${response.status}`);
        const payload = await response.json();
        const markets = extractLiveMarkets(payload);
        const byTicker: Record<string, LiveMarket> = {};
        for (const market of markets) {
          if (market.ticker) byTicker[market.ticker] = market;
        }
        if (!cancelled && Object.keys(byTicker).length) {
          setLiveMarkets(byTicker);
          setMarketCount(Object.keys(byTicker).length);
          setLiveStatus("live");
        }
      } catch {
        if (!cancelled) setLiveStatus("snapshot");
      }
    }

    loadLiveMarkets();
    const interval = window.setInterval(loadLiveMarkets, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const contracts = useMemo(() => buildBetContracts(entries, Object.values(liveMarkets)), [entries, liveMarkets]);

  const scenarioPlans = useMemo(
    () => scenarios.map((scenario) => buildScenarioPlan(scenario, contracts, budget)),
    [budget, contracts],
  );

  const bestPlan = scenarioPlans[1] || scenarioPlans[0];

  return (
    <div className="page betting-page">
      <section className="betting-hero surface">
        <div>
          <div className="eyebrow">
            <PieChart size={15} />
            Kalshi allocation lab
          </div>
          <h2 className="hero-title">
            Bet the <span>edge</span>, not the hype
          </h2>
          <p className="hero-subtitle">
            A paper portfolio across Eurovision winner, jury winner, televote winner, top 3, top 5, top 10, and YES/NO contracts. It sizes only hypothetical
            contracts from model probability versus Kalshi ask price; no real orders are ever placed.
          </p>
          <div className="market-scope">
            {["Winner", "Jury", "Televote", "Top 3", "Top 5", "Top 10", "YES", "NO"].map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
        </div>
        <div className="budget-panel">
          <div className="section-title">
            <h3>Budget</h3>
            <span>
              {liveStatus === "live" ? `${marketCount} live markets` : liveStatus === "loading" ? "loading prices" : `snapshot · ${dateTime(generatedAt)}`}
            </span>
          </div>
          <div className="budget-value">{money(budget)}</div>
          <input
            className="budget-slider"
            type="range"
            min="100"
            max="10000"
            step="50"
            value={budget}
            onChange={(event) => setBudget(Number(event.target.value))}
          />
          <div className="budget-input-row">
            <button type="button" onClick={() => setBudget(500)}>
              $500
            </button>
            <button type="button" onClick={() => setBudget(1000)}>
              $1k
            </button>
            <button type="button" onClick={() => setBudget(2500)}>
              $2.5k
            </button>
            <button type="button" onClick={() => setBudget(5000)}>
              $5k
            </button>
          </div>
        </div>
      </section>

      <section className="portfolio-summary">
        {scenarioPlans.map((plan) => (
          <article key={plan.id} className="surface scenario-card">
            <div className="section-title">
              <h3>{plan.name}</h3>
              <span>{plan.rows.length} positions</span>
            </div>
            <p>{plan.tagline}</p>
            <div className="scenario-metrics">
              <Metric label="Expected payout" value={money(plan.expectedPayout)} />
              <Metric label="Expected profit" value={money(plan.expectedProfit)} />
              <Metric label="Expected ROI" value={signed(plan.expectedRoi * 100, 1) + "%"} />
            </div>
            <div className="allocation-bars">
              {plan.rows.map((row) => (
                <div key={row.entry.country_id} className="allocation-row">
                  <span>{row.entry.flagEmoji} {row.entry.country} · {row.familyLabel} {row.side}</span>
                  <div className="bar-track">
                    <span className="bar-fill" style={{ width: `${row.weight * 100}%` }} />
                  </div>
                  <strong>{money(row.amount)}</strong>
                </div>
              ))}
            </div>
          </article>
        ))}
      </section>

      <section className="surface table-shell">
        <div className="section-title">
          <h2>{bestPlan.name} Ticket</h2>
          <span>paper contracts only</span>
        </div>
        <div className="score-table-wrap betting-table-wrap">
          <table className="score-table">
            <thead>
              <tr>
                <th>Contract</th>
                <th className="num">Stake</th>
                <th className="num">Kalshi</th>
                <th className="num">Model</th>
                <th className="num">Edge</th>
                <th className="num">Contracts</th>
                <th className="num">Gross if wins</th>
                <th className="num">Net if wins</th>
              </tr>
            </thead>
            <tbody>
              {bestPlan.rows.map((row) => (
                <tr key={row.contract.id}>
                  <td>
                    <div className="country-cell">
                      <img className="flag" src={row.entry.flagUrl} alt="" />
                      <span className="country-line">
                        <strong>{row.entry.country} · {row.familyLabel} {row.side}</strong>
                        <span>{row.contract.label} · {row.contract.source}</span>
                      </span>
                    </div>
                  </td>
                  <td className="num">{money(row.amount)}</td>
                  <td className="num">{pct(row.price)}</td>
                  <td className="num">{pct(row.contract.modelProbability)}</td>
                  <td className="num">{signed(row.edge * 100, 1)} pts</td>
                  <td className="num">{number(row.contracts, 0)}</td>
                  <td className="num">{money(row.grossIfWins)}</td>
                  <td className="num">{money(row.netIfWins)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function statusLabel(status: StreamStatus) {
  if (status === "live") return "Live";
  if (status === "connecting") return "Connecting";
  if (status === "ended") return "Reopening";
  if (status === "error") return "Error";
  return "Reconnecting";
}

function shortTime(value: string) {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return "just now";
  return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function normalizeFirehoseItem(event: MessageEvent, rules: FirehoseRule[]): NewsFlowItem | null {
  try {
    const payload = JSON.parse(event.data);
    const document = payload?.document || {};
    const url = typeof document.url === "string" ? document.url : "";
    const rule = rules.find((candidate) => String(candidate.id) === String(payload?.query_id));

    return {
      id: event.lastEventId || `${payload?.query_id || "rule"}-${payload?.matched_at || Date.now()}-${url}`,
      queryId: String(payload?.query_id || ""),
      ruleTag: rule?.tag || "",
      matchedAt: payload?.matched_at || new Date().toISOString(),
      publishTime: document.publish_time || "",
      title: document.title || url || "Untitled Firehose match",
      url,
      domain: domainFromUrl(url),
      language: document.language || "",
      categories: Array.isArray(document.page_category) ? document.page_category : [],
      types: Array.isArray(document.page_types) ? document.page_types : [],
      snippet: extractFirehoseSnippet(document),
    };
  } catch {
    return null;
  }
}

function extractFirehoseSnippet(document: any) {
  const diffChunks = Array.isArray(document?.diff?.chunks) ? document.diff.chunks : [];
  const inserted = diffChunks
    .filter((chunk: any) => chunk?.typ === "ins" && typeof chunk?.text === "string")
    .map((chunk: any) => chunk.text)
    .join(" ");
  const source = inserted || (typeof document?.markdown === "string" ? document.markdown : "");
  return source.replace(/\s+/g, " ").trim().slice(0, 360);
}

function domainFromUrl(value: string) {
  try {
    return new URL(value).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function countryHitText(item: NewsFlowItem, entry: Entry | null) {
  if (!entry) return false;
  const haystack = normalizeText(`${item.title} ${item.snippet} ${item.url}`);
  const country = normalizeText(entry.country);
  const artist = normalizeText(entry.artist);
  const song = normalizeText(entry.song);
  return haystack.includes(country) || (artist.length > 3 && haystack.includes(artist)) || (song.length > 3 && haystack.includes(song));
}

function buildCountryPulse(items: NewsFlowItem[], entries: Entry[]) {
  const rows = entries
    .map((entry) => ({
      entry,
      count: items.filter((item) => countryHitText(item, entry)).length,
    }))
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count || a.entry.predicted_rank - b.entry.predicted_rank)
    .slice(0, 10);
  const max = Math.max(1, ...rows.map((row) => row.count));
  return rows.map((row) => ({ ...row, share: row.count / max }));
}

function toMarketPrice(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(String(value).replace("$", ""));
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed > 1 ? parsed / 100 : parsed;
}

function normalizeText(value: string | null | undefined) {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .toLowerCase();
}

function extractLiveMarkets(payload: any): LiveMarket[] {
  const events = Array.isArray(payload?.events) ? payload.events : [payload];
  const markets: LiveMarket[] = [];

  for (const eventPayload of events) {
    const event = eventPayload?.event || eventPayload;
    const eventTicker = event?.ticker || eventPayload?.event_ticker || null;
    const eventMarkets = event?.markets || eventPayload?.markets || [];
    for (const market of eventMarkets) {
      markets.push({ ...market, event_ticker: market.event_ticker || eventTicker });
    }
  }

  return markets;
}

function marketFamily(market: LiveMarket): MarketFamily | null {
  const haystack = normalizeText(`${market.event_ticker || ""} ${market.ticker || ""} ${market.title || ""}`);
  if (haystack.includes("jury")) return "jury";
  if (haystack.includes("telev")) return "televote";
  if (haystack.includes("top3") || haystack.includes("top 3")) return "top3";
  if (haystack.includes("top5") || haystack.includes("top 5")) return "top5";
  if (haystack.includes("top10") || haystack.includes("top 10")) return "top10";
  if (haystack.includes("eurovision")) return "winner";
  return null;
}

function familyLabel(family: MarketFamily) {
  if (family === "top3") return "Top 3";
  if (family === "top5") return "Top 5";
  if (family === "top10") return "Top 10";
  if (family === "jury") return "Jury winner";
  if (family === "televote") return "Televote winner";
  return "Winner";
}

function modelProbability(entry: Entry, family: MarketFamily) {
  if (family === "top3") return entry.top3_probability;
  if (family === "top5") return entry.top5_probability;
  if (family === "top10") return entry.top10_probability;
  if (family === "jury") return entry.jury_winner_probability;
  if (family === "televote") return entry.audience_winner_probability;
  return entry.winner_probability;
}

function entryForMarket(market: LiveMarket, entries: Entry[]) {
  const marketText = normalizeText([market.yes_sub_title, market.subtitle, market.no_sub_title, market.title, market.ticker].filter(Boolean).join(" "));
  return entries.find((entry) => {
    const country = normalizeText(entry.country);
    const countryId = normalizeText(entry.country_id);
    return marketText.includes(country) || marketText.includes(countryId);
  });
}

function buildBetContracts(entries: Entry[], liveMarkets: LiveMarket[]): BetContract[] {
  const contracts: BetContract[] = [];
  const liveTickers = new Set<string>();

  for (const market of liveMarkets) {
    const family = marketFamily(market);
    const entry = entryForMarket(market, entries);
    if (!family || !entry || !market.ticker) continue;

    liveTickers.add(market.ticker);
    const yesAsk = toMarketPrice(market.yes_ask_dollars ?? market.yes_ask);
    const noAsk = toMarketPrice(market.no_ask_dollars ?? market.no_ask) ?? inversePrice(market.yes_bid_dollars ?? market.yes_bid);
    const probability = modelProbability(entry, family);

    if (yesAsk) contracts.push(makeContract(market.ticker, entry, family, "YES", probability, yesAsk, "live ask"));
    if (noAsk) contracts.push(makeContract(market.ticker, entry, family, "NO", 1 - probability, noAsk, "live ask"));
  }

  // Keep a winner snapshot fallback so the paper page is still useful if Kalshi live markets fail.
  for (const entry of entries) {
    const ticker = entry.kalshi_market_ticker;
    if (!ticker || liveTickers.has(ticker)) continue;
    const price = entry.kalshi_yes_ask || entry.kalshi_last_price;
    if (!price) continue;
    contracts.push(makeContract(ticker, entry, "winner", "YES", entry.winner_probability, price, "synthetic snapshot"));
  }

  return contracts;
}

function inversePrice(value: number | string | null | undefined) {
  const parsed = toMarketPrice(value);
  return parsed === null ? null : clampPrice(1 - parsed);
}

function makeContract(
  ticker: string,
  entry: Entry,
  family: MarketFamily,
  side: ContractSide,
  modelProbabilityValue: number,
  marketPrice: number,
  source: BetContract["source"],
): BetContract {
  const price = clampPrice(marketPrice);
  const probability = clampPrice(modelProbabilityValue);
  const edge = probability - price;

  return {
    id: `${ticker}-${side}`,
    ticker,
    entry,
    family,
    label: `${entry.country} ${familyLabel(family)}`,
    side,
    modelProbability: probability,
    marketPrice: price,
    edge,
    edgeRatio: probability / price,
    source,
  };
}

function clampPrice(value: number) {
  return Math.min(0.99, Math.max(0.01, value));
}

function money(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: value >= 100 ? 0 : 2,
  }).format(value);
}

function buildScenarioPlan(scenario: Scenario, contracts: BetContract[], budget: number) {
  const deduped = new Map<string, { contract: BetContract; score: number }>();
  for (const contract of contracts) {
    if (!scenario.allowNo && contract.side === "NO") continue;
    if (contract.modelProbability < scenario.minModelProbability || contract.edge < scenario.minEdge || contract.marketPrice >= 0.98) continue;
    const score = scenario.score(contract);
    if (score <= 0) continue;
    const existing = deduped.get(contract.ticker);
    if (!existing || score > existing.score) deduped.set(contract.ticker, { contract, score });
  }

  const candidates = Array.from(deduped.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, scenario.maxPositions);

  const fallback = candidates.length
    ? candidates
    : contracts.slice(0, scenario.maxPositions).map((contract) => ({ contract, score: Math.max(contract.edge, 0.001) }));

  const weights = capWeights(fallback.map((row) => row.score), scenario.maxWeight);
  const rows = fallback
    .map(({ contract }, index) => {
      const weight = weights[index];
      const amount = budget * weight;
      const price = contract.marketPrice;
      const contracts = amount / price;
      const grossIfWins = contracts;
      const netIfWins = grossIfWins - budget;
      return {
        contract,
        entry: contract.entry,
        familyLabel: familyLabel(contract.family),
        side: contract.side,
        weight,
        amount,
        price,
        edge: contract.edge,
        contracts,
        grossIfWins,
        netIfWins,
      };
    })
    .filter((row) => row.amount >= 1)
    .sort((a, b) => b.amount - a.amount);

  const expectedPayout = rows.reduce((sum, row) => sum + row.grossIfWins * row.contract.modelProbability, 0);
  const expectedProfit = expectedPayout - budget;

  return {
    id: scenario.id,
    name: scenario.name,
    tagline: scenario.tagline,
    rows,
    expectedPayout,
    expectedProfit,
    expectedRoi: expectedProfit / budget,
  };
}

function capWeights(scores: number[], cap: number) {
  const total = scores.reduce((sum, score) => sum + score, 0) || 1;
  const weights = scores.map((score) => score / total);
  const capped = new Set<number>();

  for (let guard = 0; guard < scores.length; guard++) {
    const overCap = weights.findIndex((weight, index) => weight > cap && !capped.has(index));
    if (overCap === -1) break;
    weights[overCap] = cap;
    capped.add(overCap);

    const remainingWeight = 1 - Array.from(capped).reduce((sum, index) => sum + weights[index], 0);
    const remainingScore = scores.reduce((sum, score, index) => (capped.has(index) ? sum : sum + score), 0) || 1;
    for (let index = 0; index < scores.length; index++) {
      if (!capped.has(index)) weights[index] = (scores[index] / remainingScore) * remainingWeight;
    }
  }

  return weights;
}

function AmbientField() {
  return (
    <div className="ambient-field">
      <DotField
        dotRadius={1.4}
        dotSpacing={18}
        cursorRadius={420}
        bulgeStrength={42}
        glowRadius={220}
        sparkle={false}
        waveAmplitude={0.25}
        gradientFrom="rgba(246, 199, 86, 0.2)"
        gradientTo="rgba(103, 198, 219, 0.12)"
        glowColor="rgba(246, 199, 86, 0.2)"
      />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric-tile">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

function TensionList({ entries }: { entries: Entry[] }) {
  const rows = [...entries]
    .map((entry) => ({
      entry,
      delta: entry.winner_probability - (entry.kalshi_last_price || 0),
    }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 6);

  return (
    <div className="top-five">
      {rows.map(({ entry, delta }) => (
        <div key={entry.country_id} className="mini-meter" style={{ gridTemplateColumns: "92px 1fr 52px" }}>
          <span>{entry.flagEmoji} {entry.country}</span>
          <div className="bar-track">
            <div
              className="bar-fill"
              style={{
                width: `${Math.min(100, Math.abs(delta) * 240)}%`,
                background: delta >= 0 ? "var(--green)" : "var(--coral)",
              }}
            />
          </div>
          <span className="num">{signed(delta * 100, 1)}</span>
        </div>
      ))}
    </div>
  );
}

function FilterBench({
  filters,
  onChange,
  onReset,
}: {
  filters: NumericFilters;
  onChange: (key: NumericFilterKey, value: string) => void;
  onReset: () => void;
}) {
  const activeCount = Object.values(filters).filter((value) => value.trim()).length;

  return (
    <div className="filter-bench" aria-label="Numeric table filters">
      <div className="filter-bench-title">
        <SlidersHorizontal size={15} />
        <span>Column filters</span>
      </div>
      <RangeFilter label="Win %" minKey="winMin" maxKey="winMax" min={filters.winMin} max={filters.winMax} onChange={onChange} />
      <RangeFilter label="Kalshi %" minKey="kalshiMin" maxKey="kalshiMax" min={filters.kalshiMin} max={filters.kalshiMax} onChange={onChange} />
      <RangeFilter label="Spotify" minKey="spotifyMin" maxKey="spotifyMax" min={filters.spotifyMin} max={filters.spotifyMax} onChange={onChange} />
      <RangeFilter label="YouTube" minKey="youtubeMin" maxKey="youtubeMax" min={filters.youtubeMin} max={filters.youtubeMax} onChange={onChange} />
      <RangeFilter label="Poll" minKey="pollMin" maxKey="pollMax" min={filters.pollMin} max={filters.pollMax} onChange={onChange} />
      <RangeFilter label="Jury rank" minKey="juryMin" maxKey="juryMax" min={filters.juryMin} max={filters.juryMax} onChange={onChange} />
      <RangeFilter label="Audience rank" minKey="audienceMin" maxKey="audienceMax" min={filters.audienceMin} max={filters.audienceMax} onChange={onChange} />
      <button className="reset-filters" type="button" onClick={onReset} disabled={activeCount === 0}>
        <RotateCcw size={14} />
        Reset {activeCount ? `(${activeCount})` : ""}
      </button>
    </div>
  );
}

function RangeFilter({
  label,
  minKey,
  maxKey,
  min,
  max,
  onChange,
}: {
  label: string;
  minKey: NumericFilterKey;
  maxKey: NumericFilterKey;
  min: string;
  max: string;
  onChange: (key: NumericFilterKey, value: string) => void;
}) {
  return (
    <div className="range-filter">
      <span>{label}</span>
      <input
        value={min}
        onInput={(event) => onChange(minKey, event.currentTarget.value)}
        onChange={(event) => onChange(minKey, event.target.value)}
        placeholder="min"
        inputMode="decimal"
      />
      <input
        value={max}
        onInput={(event) => onChange(maxKey, event.currentTarget.value)}
        onChange={(event) => onChange(maxKey, event.target.value)}
        placeholder="max"
        inputMode="decimal"
      />
    </div>
  );
}

function ScoreTable({
  entries,
  selectedId,
  sort,
  sortDir,
  onSort,
  onSelect,
}: {
  entries: Entry[];
  selectedId: string;
  sort: SortKey;
  sortDir: SortDir;
  onSort: (sort: SortKey) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="score-table-wrap">
      <table className="score-table">
        <thead>
          <tr>
            <SortableTh label="Rank" sortKey="rank" activeSort={sort} sortDir={sortDir} onSort={onSort} />
            <SortableTh label="Country" sortKey="country" activeSort={sort} sortDir={sortDir} onSort={onSort} />
            <SortableTh label="Win" sortKey="win" activeSort={sort} sortDir={sortDir} onSort={onSort} align="right" />
            <SortableTh label="Top 3" sortKey="top3" activeSort={sort} sortDir={sortDir} onSort={onSort} align="right" />
            <SortableTh label="Jury" sortKey="jury" activeSort={sort} sortDir={sortDir} onSort={onSort} align="right" />
            <SortableTh label="Audience" sortKey="audience" activeSort={sort} sortDir={sortDir} onSort={onSort} align="right" />
            <SortableTh label="Kalshi" sortKey="market" activeSort={sort} sortDir={sortDir} onSort={onSort} align="right" />
            <SortableTh label="Odds" sortKey="odds" activeSort={sort} sortDir={sortDir} onSort={onSort} align="right" />
            <SortableTh label="Poll" sortKey="poll" activeSort={sort} sortDir={sortDir} onSort={onSort} align="right" />
            <SortableTh label="Spotify" sortKey="spotify" activeSort={sort} sortDir={sortDir} onSort={onSort} align="right" />
            <SortableTh label="YouTube" sortKey="youtube" activeSort={sort} sortDir={sortDir} onSort={onSort} align="right" />
            <th>Manual read</th>
          </tr>
        </thead>
        <tbody>
          <AnimatePresence initial={false}>
            {entries.map((entry) => (
              <motion.tr
                key={entry.country_id}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={entry.country_id === selectedId ? "active" : ""}
                onClick={() => onSelect(entry.country_id)}
              >
                <td>
                  <span className="rank-token">{entry.predicted_rank}</span>
                </td>
                <td>
                  <div className="country-cell">
                    <img className="flag" src={entry.flagUrl} alt="" />
                    <span className="country-line">
                      <strong>{entry.country}</strong>
                      <span>{entry.song} · {entry.artist}</span>
                    </span>
                  </div>
                </td>
                <td className="num">{pct(entry.winner_probability)}</td>
                <td className="num">{pct(entry.top3_probability)}</td>
                <td className="num">{number(entry.estimated_jury_rank, 1)}</td>
                <td className="num">{number(entry.estimated_audience_rank, 1)}</td>
                <td className="num">{pct(entry.kalshi_last_price || 0)}</td>
                <td className="num">{entry.ew_odds_rank ? `#${entry.ew_odds_rank}` : "—"}</td>
                <td className="num">{compact(entry.poll_votes)}</td>
                <td className="num">{compact(entry.spotify_stream_count)}</td>
                <td className="num">{compact(entry.youtube_total_views)}</td>
                <td>
                  <span className={entry.manual_win_read.includes("bottom") ? "pill coral" : entry.manual_win_read.includes("winner") ? "pill gold" : "pill"}>
                    {entry.manual_win_read.replaceAll("_", " ")}
                  </span>
                </td>
              </motion.tr>
            ))}
          </AnimatePresence>
        </tbody>
      </table>
    </div>
  );
}

function SortableTh({
  label,
  sortKey,
  activeSort,
  sortDir,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: SortKey;
  activeSort: SortKey;
  sortDir: SortDir;
  onSort: (sort: SortKey) => void;
  align?: "left" | "right";
}) {
  const active = activeSort === sortKey;

  return (
    <th className={align === "right" ? "num" : undefined}>
      <button className={`sort-header ${active ? "active" : ""} ${align === "right" ? "right" : ""}`} type="button" onClick={() => onSort(sortKey)}>
        <span>{label}</span>
        {active ? (sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : null}
      </button>
    </th>
  );
}

function InspectorDrawer({ entry, onClose }: { entry: Entry; onClose: () => void }) {
  return (
    <motion.div
      className="inspector-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`${entry.country} country inspector`}
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
    >
      <button className="inspector-backdrop" aria-label="Close country inspector" onClick={onClose} />
      <CountryInspector entry={entry} onClose={onClose} />
    </motion.div>
  );
}

function CountryInspector({ entry, onClose }: { entry: Entry; onClose: () => void }) {
  const official = entry.videos.find((video) => video.video_type === "official_or_national");
  const semi = entry.videos.find((video) => video.video_type === "semi_final_live");
  const juryAudienceGap = entry.jury_score - entry.audience_score;

  return (
    <motion.aside
      className="surface inspector"
      key={entry.country_id}
      onClick={(event) => event.stopPropagation()}
      initial={{ opacity: 0, x: 36 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 36 }}
      transition={{ duration: 0.24 }}
    >
      <button className="inspector-close" type="button" aria-label="Close country inspector" onClick={onClose}>
        <X size={18} />
      </button>
      <div className="inspector-hero">
        <img src={entry.heroImage} alt={`${entry.country} performance`} />
        <div className="inspector-heading">
          <span className="pill gold">{rankLabel(entry.predicted_rank)} · {pct(entry.winner_probability)} win</span>
          <h2>{entry.flagEmoji} {entry.country}</h2>
        </div>
      </div>

      <div className="section-title">
        <h3>{entry.song}</h3>
        <span>{entry.artist}</span>
      </div>

      <div className="signal-grid">
        <Signal label="Estimated position" value={number(entry.estimated_position, 2)} icon={<Gauge size={15} />} />
        <Signal label="Running order" value={`#${entry.running_order_final}`} icon={<CalendarClock size={15} />} />
        <Signal label="Manual jury" value={`${entry.manual_jury}/10`} icon={<Trophy size={15} />} />
        <Signal label="Manual audience" value={`${entry.manual_audience}/10`} icon={<Users size={15} />} />
        <Signal label="Kalshi" value={pct(entry.kalshi_last_price || 0)} icon={<LineChart size={15} />} />
        <Signal label="Poll votes" value={compact(entry.poll_votes)} icon={<Heart size={15} />} />
      </div>

      <p className="notes">{entry.manual_notes}</p>

      <div className="viz-panel">
        <div className="section-title">
          <h3>Signal Shape</h3>
          <span>normalized 0-1</span>
        </div>
        <SignalRadar entry={entry} />
      </div>

      <div className="viz-panel">
        <div className="section-title">
          <h3>Probability Lanes</h3>
          <span>20k simulations</span>
        </div>
        <ProbabilityBars entry={entry} />
      </div>

      <div className="viz-panel">
        <div className="section-title">
          <h3>Kalshi Motion</h3>
          <span>{entry.kalshiCandles.length} candles</span>
        </div>
        <Sparkline candles={entry.kalshiCandles} />
      </div>

      <div className="viz-panel">
        <div className="section-title">
          <h3>Friend Vote Gravity</h3>
          <span>inbound avg points</span>
        </div>
        <SynergyBars synergies={entry.inboundSynergies} />
      </div>

      <div className="viz-panel">
        <div className="section-title">
          <h3>Media</h3>
          <span>{compact(entry.youtube_total_views)} views</span>
        </div>
        <div className="media-grid">
          {[official, semi].filter(Boolean).map((video) => (
            <a key={video!.youtube_id} className="media-item" href={video!.url} target="_blank" rel="noreferrer">
              <img src={video!.thumbnail_url} alt="" />
              <span className="country-line">
                <strong><Play size={12} /> {video!.video_type === "semi_final_live" ? "Semi-final" : "Official / national"}</strong>
                <span>{compact(video!.views)} views · {compact(video!.likes)} likes · {compact(video!.comments)} comments</span>
              </span>
            </a>
          ))}
        </div>
      </div>

      <div className="viz-panel">
        <div className="section-title">
          <h3>History</h3>
          <span>{entry.participations} participations</span>
        </div>
        <div className="signal-grid">
          <Signal label="Victories" value={number(entry.victories_count)} icon={<Trophy size={15} />} />
          <Signal label="Semi quals" value={`${number(entry.semi_qualifications)}/${number(entry.semi_participations)}`} icon={<Activity size={15} />} />
        </div>
        <div className="notes">
          Recent finals:{" "}
          {entry.recentHistory
            .filter((row) => row.final_place)
            .slice(0, 5)
            .map((row) => `${row.year}: #${row.final_place}`)
            .join(" · ") || "No recent final placements in source window."}
        </div>
      </div>

      <div className="viz-panel">
        <div className="section-title">
          <h3>Split Diagnosis</h3>
          <span>{juryAudienceGap >= 0 ? "jury lean" : "audience lean"}</span>
        </div>
        <div className="signal-grid">
          <Signal label="Jury score" value={number(entry.jury_score, 3)} icon={<Trophy size={15} />} />
          <Signal label="Audience score" value={number(entry.audience_score, 3)} icon={<Music2 size={15} />} />
          <Signal label="Qualitative adj." value={signed(entry.qualitative_adjustment, 3)} icon={<Sparkles size={15} />} />
          <Signal label="Political" value={entry.manual_political_impact} icon={<Users size={15} />} />
        </div>
      </div>
    </motion.aside>
  );
}

function Signal({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="signal-box">
      <div className="label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {icon}
        {label}
      </div>
      <div className="value">{value}</div>
    </div>
  );
}

export default App;
